import { walk, type Element, type FrameElement, type Screen } from "../../../shared/page-model/vocabulary.ts";
import { workspaceUrl } from "./useWorkspaceJson.ts";

/**
 * The page editor's logic that needs no React, kept in a plain .ts file so it runs under
 * `node --test` directly, matching formFields.ts's own rationale.
 */

const SOURCE = /^products\/([a-z0-9][a-z0-9_-]*)\/screens\/[a-z0-9][a-z0-9_-]*\.json$/;

/** The product slug a screen's source path names, or undefined for a path outside products/. */
export function productSlug(source: string): string | undefined {
	return SOURCE.exec(source)?.[1];
}

/** An image's src is product-relative, so the workspace URL is the product directory plus src. */
export function imageUrl(sessionId: string, source: string, src: string): string {
	const slug = productSlug(source);
	return workspaceUrl(sessionId, slug ? `products/${slug}/${src}` : src);
}

/** The chain from the root to the element, or nothing when the id is not in the screen. */
export function ancestors(screen: Screen, id: string): Element[] {
	const parents = new Map<string, FrameElement | null>();
	const byId = new Map<string, Element>();
	for (const [node, parent] of walk(screen.root)) {
		parents.set(node.id, parent);
		byId.set(node.id, node);
	}
	if (!byId.has(id)) return [];
	const chain: Element[] = [];
	for (let cursor: Element | null | undefined = byId.get(id); cursor; cursor = parents.get(cursor.id) ?? null) chain.unshift(cursor);
	return chain;
}

export function parentOf(screen: Screen, id: string): FrameElement | null {
	for (const [node, parent] of walk(screen.root)) if (node.id === id) return parent;
	return null;
}

export function elementOf(screen: Screen, id: string): Element | undefined {
	for (const [node] of walk(screen.root)) if (node.id === id) return node;
	return undefined;
}

/** What the breadcrumb calls an element: its kind and a short description. */
export function describe(element: Element): string {
	switch (element.kind) {
		case "text":
			return `文字 “${firstWords(element.text)}”`;
		case "button":
			return `按钮 “${element.label}”`;
		case "image":
			return `图片 ${element.width}×${element.height}`;
		case "frame":
			return `容器（${element.children?.length ?? 0} 项）`;
	}
}

function firstWords(text: string): string {
	const line = text.split("\n")[0] ?? "";
	return line.length > 24 ? `${line.slice(0, 24)}…` : line;
}

/** Where 添加 puts a new element: the end of a selected frame, after a selected element, or the end of the root. */
export function addTarget(screen: Screen, selected: string | null): { parent: string; index: number } {
	const element = selected ? elementOf(screen, selected) : undefined;
	if (!element || element.id === screen.root.id) return { parent: screen.root.id, index: screen.root.children?.length ?? 0 };
	if (element.kind === "frame") return { parent: element.id, index: element.children?.length ?? 0 };
	const parent = parentOf(screen, element.id)!;
	return { parent: parent.id, index: (parent.children ?? []).indexOf(element) + 1 };
}

export function draftKey(callId: string): string {
	return `page-editor:${callId}`;
}

export function sourceDraftKey(source: string): string {
	return `page-editor:source:${source}`;
}

/** CSS-order padding as four sides. */
export function padding4(value: number | number[]): [number, number, number, number] {
	const v = Array.isArray(value) ? value : [value];
	if (v.length === 1) return [v[0]!, v[0]!, v[0]!, v[0]!];
	if (v.length === 2) return [v[0]!, v[1]!, v[0]!, v[1]!];
	return [v[0]!, v[1]!, v[2]!, v[3]!];
}

/** Four sides back to the shortest CSS form the model keeps. */
export function paddingFrom4([top, right, bottom, left]: [number, number, number, number]): number | number[] {
	if (top === right && right === bottom && bottom === left) return top;
	if (top === bottom && right === left) return [top, right];
	return [top, right, bottom, left];
}

/** Hyphenated declarations from css.ts as a React style object. */
export function styleObject(declarations: Record<string, string>): Record<string, string> {
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(declarations)) out[key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())] = value;
	return out;
}
